import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import type { IAlert } from 'common/interfaces/IAlert'
import { DateService } from 'common/services/dateService'
import { PathUtils } from 'routes/routes.utils'

import { useLazyFetchSeriesRunQuery } from '../../api/series.api'
import { isEmrAccessDenied, SERIES_ERROR_CODES, toSeriesApiError } from '../../api/series.transform'
import type { SeriesApiError, SeriesListRow } from '../../api/series.types'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'
import { pickDetailsVisit } from './pickDetailsVisit'

const TOASTS = SERIES_PAGES_TEXT.toasts
const NOTICE_TITLE = 'Series'
const NOT_FOUND_STATUS = 404
const NOT_FOUND_CODE = 'not_found'
const APPOINTMENT_PARAM = 'appointment'
const TAB_PARAM = 'tab'
const SERIES_TAB = 'series'
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

type Args = { siteId: string; onGone: () => void }

type SeriesDetails = {
  openDetails: (row: SeriesListRow) => void
  loadingId: number | null
}

const notice = (type: ALERT_CONSTANTS, description: string): IAlert => ({
  title: NOTICE_TITLE,
  type,
  description,
})

const isGone = (error: SeriesApiError): boolean =>
  error.status === NOT_FOUND_STATUS || error.code === NOT_FOUND_CODE

const detailsFailureText = (failure: SeriesApiError): string => {
  if (isEmrAccessDenied(failure)) return TOASTS.emrAccessDenied
  return failure.code === SERIES_ERROR_CODES.EMR_UNAVAILABLE ? TOASTS.emrNoAnswer : failure.message
}

const calendarLink = (siteId: string, date: string, appointmentId: number): string => {
  const query = new URLSearchParams({
    [APPOINTMENT_PARAM]: String(appointmentId),
    [TAB_PARAM]: SERIES_TAB,
  })
  return `${PathUtils.getSchedule(siteId, date)}?${query.toString()}`
}

// Details: load the run (warming the dialog's cache), pick a visit and open it on the calendar.
export const useSeriesDetails = ({ siteId, onGone }: Args): SeriesDetails => {
  const [fetchRun] = useLazyFetchSeriesRunQuery()
  const navigate = useNavigate()
  const { timeZone } = useTimezone()
  const { setNotification } = useNotification()
  const [loadingId, setLoadingId] = useState<number | null>(null)
  const isLoadingRef = useRef(false)

  const openDetails = async (row: SeriesListRow) => {
    if (isLoadingRef.current || !timeZone) return
    isLoadingRef.current = true
    setLoadingId(row.id)
    try {
      const run = await fetchRun({ siteId, id: row.id }).unwrap()
      const today = DateService.getCurrentTimeInTimezone(timeZone).format(
        DATE_FORMAT.PATIENT_TRACKER_DATE,
      )
      const visit = pickDetailsVisit(run?.visits ?? [], today)
      if (!visit?.date || !ISO_DATE.test(visit.date) || visit.appointmentId === null) {
        setNotification(notice(ALERT_CONSTANTS.WARNING, TOASTS.noVisitBooked))
        return
      }
      navigate(calendarLink(siteId, visit.date, visit.appointmentId))
    } catch (error) {
      const failure = toSeriesApiError(error)
      if (isGone(failure)) {
        setNotification(notice(ALERT_CONSTANTS.ERROR, TOASTS.seriesGone))
        onGone()
        return
      }
      setNotification(notice(ALERT_CONSTANTS.ERROR, detailsFailureText(failure)))
    } finally {
      isLoadingRef.current = false
      setLoadingId(null)
    }
  }

  return {
    openDetails: (row) => {
      void openDetails(row)
    },
    loadingId,
  }
}
